import { Actions, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { concat as observableConcat, Observable, of as observableOf } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';

export interface PaginationMeta {
  limit: number;
  totalCount: number;
  page: number;
}

export interface ResourceState<Data> {
  data: Data | null;
  fetchDone: boolean;
  fetchLoading: boolean;
  fetchFailed: boolean;
  error?: any;
}

export type LazyLoadedResourceState<T> = ResourceState<T[]> & {
  data: T[];
  latestMeta: PaginationMeta | null;
};

export interface Editable<T> {
  saving?: boolean;
  editing?: boolean;
  deleting?: boolean;
  editable?: boolean;
  writable?: boolean;
  errors?: string[];
  error?: null | { errorData: { error: string[]; errorFields: { [key: string]: string } } };
  value: T | null;
}

export const initialResourceState = {
  data: null,
  fetchDone: false,
  fetchLoading: false,
  fetchFailed: false,
};

export function getInitialResourceState<D>(): Omit<typeof initialResourceState, 'data'> & {
  data: D | typeof initialResourceState['data'];
} {
  return initialResourceState;
}

export interface ActionWithPayload<T> extends Action {
  payload: T;
}

export function generateUuid(): string {
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string): string => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0,
      // eslint-disable-next-line no-bitwise
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

  return uuid;
}

export function buildResourceActions<Data>({
  LOAD = 'UNDEFINED_LOAD',
  FETCH,
}: {
  LOAD?: string;
  FETCH: string;
}): { load: (data: Data) => Action; fetch: () => Action } {
  return {
    load: function (data: Data): Action {
      return <Action>(<any>{ type: LOAD, data });
    },
    fetch: function (): Action {
      return <Action>(<any>{ type: FETCH });
    },
  };
}

export function buildResourceEpicV2<
  CanonicalData extends {},
  StartAction extends Action,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  SuccessAction extends Action,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  FailureAction extends Action
>({
  cancelable = true,
  actions$,
  FETCH_START,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  getResourceData,
}: {
  cancelable?: boolean;
  actions$: Actions<Action>;
  FETCH_START: string;
  FETCH_SUCCESS: string;
  FETCH_FAILURE: string;
  getResourceData: (action: StartAction) => Observable<CanonicalData>;
}): Observable<Action> {
  const startAction$ = actions$.pipe(ofType<StartAction>(FETCH_START));
  const epic = (action: StartAction) =>
    getResourceData(action).pipe(
      map(payload => ({
        type: FETCH_SUCCESS,
        payload,
      })),
      catchError(({ error }) =>
        observableOf({ type: FETCH_FAILURE, payload: { errorData: error } }),
      ),
    );

  return cancelable
    ? startAction$.pipe(switchMap(action => epic(action).pipe(takeUntil(startAction$))))
    : startAction$.pipe(mergeMap(epic));
}

export function buildResourceEpic<Data>({
  actions$,
  FETCH,
  FETCH_START,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  getResourceData,
}: {
  actions$: Actions<Action>;
  FETCH: string;
  FETCH_START: string;
  FETCH_SUCCESS: string;
  FETCH_FAILURE: string;
  getResourceData: (action: Action) => Observable<Data>;
}): Observable<Action> {
  return actions$.pipe(
    ofType(FETCH),
    switchMap(action => {
      const data$ = getResourceData(action);
      return observableConcat(
        observableOf({ type: FETCH_START }),
        data$.pipe(
          map(data => ({ type: FETCH_SUCCESS, data })),
          catchError(error => observableOf({ type: FETCH_FAILURE, error })),
          takeUntil(actions$.pipe(ofType(FETCH))),
        ),
      );
    }),
  );
}

export function buildResourceReducer<State = any>({
  stateSelector,
  LOAD = 'UNDEFINED_LOAD',
  FETCH_START,
  FETCH_SUCCESS,
  FETCH_FAILURE,
}: {
  stateSelector: keyof State;
  LOAD?: string;
  FETCH_START: string;
  FETCH_SUCCESS: string;
  FETCH_FAILURE: string;
}) {
  return function <T extends State>(state: T, action: Action): T {
    switch ((<any>action).type) {
      case LOAD: {
        return {
          ...state,
          [stateSelector]: {
            ...state[stateSelector],
            data: (<any>action).data || (<any>action).payload,
            fetchDone: true,
          },
        };
      }

      case FETCH_START: {
        return {
          ...state,
          [stateSelector]: {
            ...state[stateSelector],
            fetchLoading: true,
            fetchFailed: false,
          },
        };
      }

      case FETCH_SUCCESS: {
        return {
          ...state,
          [stateSelector]: {
            ...state[stateSelector],
            data: (<any>action).data || (<any>action).payload,
            fetchDone: true,
            fetchLoading: false,
            fetchFailed: false,
          },
        };
      }

      case FETCH_FAILURE: {
        return {
          ...state,
          [stateSelector]: {
            ...state[stateSelector],
            fetchLoading: false,
            fetchFailed: true,
            error: (<any>action).data || (<any>action).payload,
          },
        };
      }

      default:
        return state;
    }
  };
}

export function buildLazyLoadedResourceReducer<
  State extends Record<string, LazyLoadedResourceState<any>> | Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Key = keyof State
>(
  stateSelector: keyof State,
  actions: {
    FETCH_START: string;
    CLEAR: string;
    FETCH_SUCCESS: string;
    FETCH_FAILURE: string;
  },
): (initialState: State, action: Action) => State {
  const { FETCH_START, CLEAR, FETCH_SUCCESS, FETCH_FAILURE } = actions;

  return function (state: State, action: Action): State {
    switch (action.type) {
      case FETCH_START: {
        return {
          ...state,
          [stateSelector]: {
            ...state[stateSelector],
            fetchLoading: true,
            fetchFailed: false,
          },
        };
      }

      case CLEAR: {
        return {
          ...state,
          [stateSelector]: {
            ...state[stateSelector],
            data: [],
          },
        };
      }

      case FETCH_SUCCESS: {
        const actionData: { meta: PaginationMeta; rows: any[] } =
          (<any>action).data || (<any>action).payload;
        return {
          ...state,
          [stateSelector]: {
            ...state[stateSelector],
            latestMeta: actionData.meta,
            data: [...state[stateSelector].data, ...actionData.rows],
            fetchDone: true,
            fetchLoading: false,
            fetchFailed: false,
          },
        };
      }

      case FETCH_FAILURE: {
        const actionData = (<any>action).data || (<any>action).payload;
        return {
          ...state,
          [stateSelector]: {
            ...state[stateSelector],
            fetchLoading: false,
            fetchFailed: true,
            error: actionData,
          },
        };
      }

      default:
        return state;
    }
  };
}

export function buildEditableResourceReducer<State>({
  stateSelector,
  SAVE_START,
  SAVE_FAILURE,
  SAVE_SUCCESS,
  DELETE_START,
  DELETE_FAILURE,
  DELETE_SUCCESS,
  EDIT,
  CANCEL,
}: {
  stateSelector: keyof State;
  SAVE_START: string;
  SAVE_FAILURE: string;
  SAVE_SUCCESS: string;
  DELETE_START?: string;
  DELETE_FAILURE?: string;
  DELETE_SUCCESS?: string;
  EDIT: string;
  CANCEL: string;
}): (state: State, action: Action) => State {
  return function editableReducer(state: State, action: Action): State {
    switch (action.type) {
      case SAVE_START:
        return {
          ...(<any>state),
          [stateSelector]: {
            ...(<any>state[stateSelector]),
            saving: true,
            error: null,
          },
        };

      case SAVE_SUCCESS:
        return {
          ...(<any>state),
          [stateSelector]: {
            ...(<any>state[stateSelector]),
            saving: false,
            editing: false,
          },
        };

      case SAVE_FAILURE:
        return {
          ...(<any>state),
          [stateSelector]: {
            ...(<any>state[stateSelector]),
            saving: false,
            editing: true,
            error: (<any>action).payload,
          },
        };

      case DELETE_START:
        return {
          ...(<any>state),
          [stateSelector]: {
            ...(<any>state[stateSelector]),
            deleting: true,
          },
        };

      case DELETE_SUCCESS:
        return {
          ...(<any>state),
          [stateSelector]: {
            ...(<any>state[stateSelector]),
            deleting: false,
          },
        };

      case DELETE_FAILURE:
        return {
          ...(<any>state),
          [stateSelector]: {
            ...(<any>state[stateSelector]),
            deleting: false,
            error: (<any>action).payload,
          },
        };

      case EDIT:
        return {
          ...(<any>state),
          [stateSelector]: {
            ...(<any>state[stateSelector]),
            editing: true,
          },
        };

      case CANCEL:
        return {
          ...(<any>state),
          [stateSelector]: {
            ...(<any>state[stateSelector]),
            editing: false,
            error: null,
          },
        };

      default:
        return state;
    }
  };
}

export const isResourceEmpty = <T>(source: Observable<ResourceState<T>>): Observable<boolean> =>
  source.pipe(
    filter(({ fetchDone, fetchFailed }) => fetchDone && !fetchFailed),
    map(({ data }) =>
      Array.isArray(data) ? !data.length : typeof data === 'undefined' || data === null,
    ),
  );

export const isInitialLoading = <T>(source: Observable<ResourceState<T>>): Observable<boolean> =>
  source.pipe(map(({ fetchDone, fetchLoading }) => !fetchDone && fetchLoading));
